import { apiClient } from "@/lib/api/http"

// ── Types ───────────────────────────────────────────────────────────────────────
export interface Company {
  id: string
  name: string
  legalName: string
  description: string
  tags: string[]
  email: string
  phoneNumber: string
  website: string
  logoUrl: string
  taxId: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  country: string
  verificationStatus: string
  createdAt: string
  updatedAt: string
  verification: null | {
    id: string
    companyId: string
    documentUrl: string
    documentType: string
    documentIdentifier: string
    status: string
    createdAt: string
    updatedAt: string
  }
  industry?: string
  companyType?: string
  annualRevenue?: string
  employeeCount?: string
  tradeAbility?: string
}

export interface CreateCompanyPayload {
  name: string
  legalName: string
  description: string
  tags: string[]
  email: string
  phoneNumber: string
  website: string
  logoUrl: string
  taxId: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  country: string
}

// ── Envelope wrapper ─────────────────────────────────────────────────────────────
interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

export async function createCompany(
  payload: CreateCompanyPayload,
  token: string
): Promise<Company> {
  const { data: envelope } = await apiClient.post<any>(
    "/company",
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope?.data ?? envelope
}

// ── GET /company/{id} ────────────────────────────────────────────────────────────
export async function getCompanyById(
  id: string,
  token?: string
): Promise<Company> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<Company>>(
    `/company/${id}`,
    { headers }
  )
  return envelope.data
}

// ── GET /company ─────────────────────────────────────────────────────────────────
export async function getAllCompanies(token?: string): Promise<Company[]> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<any>(
    "/company",
    { headers }
  )
  if (!envelope) return []
  if (Array.isArray(envelope.data)) {
    return envelope.data
  }
  if (envelope.data && Array.isArray(envelope.data.companies)) {
    return envelope.data.companies
  }
  if (Array.isArray(envelope)) {
    return envelope
  }
  if (envelope && Array.isArray(envelope.companies)) {
    return envelope.companies
  }
  return []
}

// ── GET /company/rfq/{rfqId} ─────────────────────────────────────────────────────
export async function getCompaniesByRfqId(rfqId: string, token: string): Promise<Company[]> {
  const { data: envelope } = await apiClient.get<any>(
    `/company/rfq/${rfqId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  if (!envelope) return []
  if (Array.isArray(envelope.data)) {
    return envelope.data
  }
  if (envelope.data && Array.isArray(envelope.data.companies)) {
    return envelope.data.companies
  }
  if (Array.isArray(envelope)) {
    return envelope
  }
  if (envelope && Array.isArray(envelope.companies)) {
    return envelope.companies
  }
  return []
}

// ── POST /company/{id}/companyDocumentUpload ─────────────────────────────────────
export async function uploadCompanyDocument(id: string, file: File, token: string): Promise<string> {
  const formData = new FormData()
  formData.append("file", file)
  
  const { data: envelope } = await apiClient.post<any>(
    `/company/${id}/companyDocumentUpload`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data"
      }
    }
  )
  return envelope?.data?.url
}

// ── POST /company/{id}/verification ──────────────────────────────────────────────
export async function verifyCompany(
  id: string,
  payload: { documentUrl: string; documentType: string; documentIdentifier: string },
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/company/${id}/verification`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── PATCH /company/{id} ───────────────────────────────────────────────────────────
export async function updateCompany(
  id: string,
  payload: any,
  token: string
): Promise<Company> {
  const { data: envelope } = await apiClient.patch<Envelope<Company>>(
    `/company/${id}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

export async function searchCompanies(name: string, token: string, signal?: AbortSignal): Promise<Company[]> {
  const { data: envelope } = await apiClient.get<Envelope<Company[]>>(
    "/company/search",
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { name },
      signal,
    }
  )
  return envelope.data
}

export interface CompanyAddressPayload {
  title: string
  addressLine1: string
  addressLine2?: string
  city: string
  cityId: string
  state: string
  postalCode: string
  country: string
  isDefault: boolean
}

export async function addCompanyAddress(
  companyId: string,
  payload: CompanyAddressPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/company/${companyId}/address`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

export async function getCompanyAddresses(
  companyId: string,
  token: string
): Promise<any[]> {
  const { data: envelope } = await apiClient.get<Envelope<any[]>>(
    `/company/${companyId}/address`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

export async function setCompanyDefaultAddress(
  companyId: string,
  addressId: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/company/${companyId}/address/${addressId}/default`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

export async function getCompanyVerificationDocUrl(
  companyId: string,
  verificationId: string,
  token: string
): Promise<{ url: string; expiresIn: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ url: string; expiresIn: number }>>(
    `/company/${companyId}/verification/${verificationId}/document-url`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}


